import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  Sparkles, Beaker, Plus, Cpu, MessageSquare, X, Loader2, Trash2,
  Pencil, ExternalLink, DollarSign, TrendingUp, Hash, LayoutGrid, GitBranch, Clock, Info,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PoC_AI — Prueba de Concepto Pando
 * Espacio de experimentación para casos de uso Pando antes de incorporarlos
 * a producción. CRUD persistente sobre `poc_ai_items`.
 */

const STATUS_OPTIONS = ["idea", "exploración", "piloto", "resultado", "producción", "descartada"];
const PIPELINE_ORDER = ["idea", "exploración", "piloto", "resultado", "producción"]; // descartada se muestra aparte
const STATUS_STYLE = {
  "idea":         "bg-gray-100 text-gray-700 border-gray-300",
  "exploración":  "bg-blue-50 text-blue-700 border-blue-300",
  "piloto":       "bg-amber-50 text-amber-700 border-amber-300",
  "resultado":    "bg-indigo-50 text-indigo-700 border-indigo-300",
  "producción":   "bg-green-50 text-green-700 border-green-300",
  "descartada":   "bg-red-50 text-red-700 border-red-300",
};
// Color sólido del nodo de la timeline para cada estado
const STATUS_DOT = {
  "idea":         "bg-gray-400",
  "exploración":  "bg-blue-500",
  "piloto":       "bg-amber-500",
  "resultado":    "bg-indigo-500",
  "producción":   "bg-green-600",
  "descartada":   "bg-red-500",
};

const EMPTY_FORM = {
  title: "",
  desc: "",
  status: "idea",
  owner: "",
  links: [],
  cost_estimate_usd: 0,
  savings_estimate_eur: 0,
  tokens_used: 0,
  model: "",
  notes: "",
  ofertas_vinculadas: [],
  proyectos_vinculados: [],
};

const fmtUSD = (n) => "$" + Number(n || 0).toFixed(2);
const fmtEUR = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 }) + " €";
const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES");

export default function PocAi() {
  const [pocs, setPocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [view, setView] = useState("cards"); // "cards" | "pipeline"
  const [detailPoc, setDetailPoc] = useState(null); // PoC abierta en modal de detalle
  const [similares, setSimilares] = useState([]); // T17 · candidatos de título parecido antes de crear
  const [ofertaQuery, setOfertaQuery] = useState("");
  const [ofertaResults, setOfertaResults] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [proyectoQuery, setProyectoQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/poc-ai`);
      setPocs(data || []);
    } catch (e) {
      toast.error("Error cargando PoCs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // T17 · Candidatos de título parecido mientras se escribe el título de
  // una PoC NUEVA (nunca al editar una ya existente). Debounce 400ms.
  useEffect(() => {
    if (editing || !showForm || form.title.trim().length < 3) { setSimilares([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/poc-ai/candidatos-similares`, { params: { title: form.title } })
        .then(r => setSimilares(r.data?.candidatos || []))
        .catch(() => setSimilares([]));
    }, 400);
    return () => clearTimeout(t);
  }, [form.title, editing, showForm]);

  // T17 · Búsqueda de ofertas reales para vincular (debounce 350ms).
  useEffect(() => {
    if (!showForm || ofertaQuery.trim().length < 3) { setOfertaResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/ofertas-comercial`, { params: { q: ofertaQuery, limit: 8 } })
        .then(r => setOfertaResults(Array.isArray(r.data) ? r.data : []))
        .catch(() => setOfertaResults([]));
    }, 350);
    return () => clearTimeout(t);
  }, [ofertaQuery, showForm]);

  // T17 · Proyectos reales (se cargan una vez al abrir el modal, filtro en cliente).
  useEffect(() => {
    if (!showForm || allProjects.length > 0) return;
    axios.get(`${API}/projects`).then(r => setAllProjects(r.data || [])).catch(() => setAllProjects([]));
  }, [showForm, allProjects.length]);
  const proyectoResults = proyectoQuery.trim().length < 2 ? [] :
    allProjects.filter(p => (p.name || "").toLowerCase().includes(proyectoQuery.toLowerCase())).slice(0, 8);

  const filtered = pocs.filter(p => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.title || "").toLowerCase().includes(q) ||
      (p.desc || "").toLowerCase().includes(q) ||
      (p.owner || "").toLowerCase().includes(q)
    );
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };
  const openEdit = (poc) => {
    setEditing(poc);
    setForm({
      title: poc.title || "",
      desc: poc.desc || "",
      status: poc.status || "idea",
      owner: poc.owner || "",
      links: poc.links || [],
      cost_estimate_usd: poc.cost_estimate_usd || 0,
      savings_estimate_eur: poc.savings_estimate_eur || 0,
      tokens_used: poc.tokens_used || 0,
      model: poc.model || "",
      notes: poc.notes || "",
      ofertas_vinculadas: poc.ofertas_vinculadas || [],
      proyectos_vinculados: poc.proyectos_vinculados || [],
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_estimate_usd: Number(form.cost_estimate_usd || 0),
        savings_estimate_eur: Number(form.savings_estimate_eur || 0),
        tokens_used: parseInt(form.tokens_used || 0, 10),
        links: (form.links || []).filter(l => l.url),
      };
      if (editing) {
        const { data } = await axios.put(`${API}/poc-ai/${editing.id}`, payload);
        setPocs(prev => prev.map(p => p.id === editing.id ? data : p));
        if (detailPoc && detailPoc.id === editing.id) setDetailPoc(data);
        toast.success("PoC actualizada");
      } else {
        const { data } = await axios.post(`${API}/poc-ai`, payload);
        setPocs(prev => [data, ...prev]);
        toast.success("PoC creada");
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando PoC");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (poc) => {
    if (!window.confirm(`¿Eliminar la PoC «${poc.title}»? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/poc-ai/${poc.id}`);
      setPocs(prev => prev.filter(p => p.id !== poc.id));
      toast.success("PoC eliminada");
    } catch (err) {
      toast.error("Error eliminando PoC");
    }
  };

  const updateLink = (idx, key, value) => {
    setForm(f => {
      const links = [...(f.links || [])];
      links[idx] = { ...links[idx], [key]: value };
      return { ...f, links };
    });
  };
  const addLink = () => setForm(f => ({ ...f, links: [...(f.links || []), { label: "", url: "" }] }));
  const removeLink = (idx) => setForm(f => ({ ...f, links: (f.links || []).filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-6" data-testid="poc-ai-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={22} className="text-purple-600" /> PoC_AI · Prueba de Concepto Pando
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Espacio de experimentación para casos de uso Pando antes de incorporarlos a producción.
            Cada PoC registra su estado, owner, métricas (coste, ahorro, tokens) y enlaces.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle de vista */}
          <div className="bg-white border border-gray-200 rounded-lg p-0.5 flex" data-testid="poc-view-toggle">
            <button
              onClick={() => setView("cards")}
              className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-all ${view === "cards" ? "bg-[#007934] text-white" : "text-gray-500 hover:bg-gray-50"}`}
              data-testid="poc-view-cards"
            >
              <LayoutGrid size={12} /> Tarjetas
            </button>
            <button
              onClick={() => setView("pipeline")}
              className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-all ${view === "pipeline" ? "bg-[#007934] text-white" : "text-gray-500 hover:bg-gray-50"}`}
              data-testid="poc-view-pipeline"
            >
              <GitBranch size={12} /> Pipeline
            </button>
          </div>
          <button
            className="inline-flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#00622a] transition-colors text-sm"
            data-testid="poc-new-btn"
            onClick={openCreate}
          >
            <Plus size={14} /> Nueva PoC
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar PoC por título, descripción u owner…"
          className="flex-1 min-w-[240px] bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#007934]"
          data-testid="poc-search"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
          data-testid="poc-filter-status"
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Stats compactos */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="poc-stats">
        {STATUS_OPTIONS.slice(0, 5).map(st => {
          const n = pocs.filter(p => p.status === st).length;
          return (
            <div key={st} className={`rounded-lg border p-3 text-center ${STATUS_STYLE[st]}`}>
              <p className="text-2xl font-bold">{n}</p>
              <p className="text-[10px] uppercase tracking-wider">{st}</p>
            </div>
          );
        })}
      </div>

      {/* Listado · Vista TARJETAS */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-44 rounded-xl loading-shimmer" />)}
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all flex flex-col" data-testid={`poc-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="p-2 rounded-lg bg-purple-50 text-purple-600"><Beaker size={16} /></div>
                <div className="flex items-center gap-1">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[p.status] || STATUS_STYLE.idea}`}>
                    {p.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setDetailPoc(p)}
                className="text-left"
                data-testid={`poc-card-detail-${p.id}`}
                title="Ver detalle e historial"
              >
                <h3 className="font-semibold text-gray-900 text-sm mb-1 hover:text-[#007934] transition-colors">{p.title}</h3>
                <p className="text-xs text-gray-600 leading-snug mb-3 line-clamp-3 flex-1">{p.desc || <span className="italic text-gray-300">Sin descripción</span>}</p>
              </button>

              {/* Métricas */}
              {(p.cost_estimate_usd > 0 || p.savings_estimate_eur > 0 || p.tokens_used > 0) && (
                <div className="grid grid-cols-3 gap-1.5 mb-3 pt-2 border-t border-gray-100">
                  <div className="text-center" title="Coste estimado de la PoC en USD">
                    <DollarSign size={11} className="mx-auto text-purple-500" />
                    <p className="text-[10px] font-semibold text-gray-700">{fmtUSD(p.cost_estimate_usd)}</p>
                    <p className="text-[9px] text-gray-400">coste Pando</p>
                  </div>
                  <div className="text-center" title="Ahorro previsto si pasa a producción">
                    <TrendingUp size={11} className="mx-auto text-green-600" />
                    <p className="text-[10px] font-semibold text-gray-700">{fmtEUR(p.savings_estimate_eur)}</p>
                    <p className="text-[9px] text-gray-400">ahorro €</p>
                  </div>
                  <div className="text-center" title="Tokens consumidos en pruebas">
                    <Hash size={11} className="mx-auto text-blue-500" />
                    <p className="text-[10px] font-semibold text-gray-700">{fmtInt(p.tokens_used)}</p>
                    <p className="text-[9px] text-gray-400">tokens</p>
                  </div>
                </div>
              )}

              {/* Enlaces */}
              {(p.links || []).length > 0 && (
                <div className="space-y-1 mb-3">
                  {(p.links || []).map((l, i) => l.url && (
                    <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                       className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:underline truncate">
                      <ExternalLink size={10} className="flex-shrink-0" />
                      <span className="truncate">{l.label || l.url}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-[11px] text-gray-500 pt-2 border-t border-gray-100 mt-auto">
                <div className="flex items-center gap-1.5 truncate">
                  <Cpu size={12} /> <span className="font-medium text-gray-700 truncate">{p.owner || "—"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(p)}
                    className="p-1 rounded text-gray-400 hover:text-[#007934] hover:bg-green-50"
                    data-testid={`poc-edit-${p.id}`}
                    title="Editar PoC"
                  ><Pencil size={13} /></button>
                  <button
                    onClick={() => remove(p)}
                    className="p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50"
                    data-testid={`poc-delete-${p.id}`}
                    title="Eliminar PoC"
                  ><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-sm text-gray-400 py-12 bg-white rounded-xl border border-gray-100" data-testid="poc-empty">
              <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
              {pocs.length === 0 ? "Aún no hay PoCs registradas. Crea la primera con «Nueva PoC»." : "Sin PoCs que coincidan con la búsqueda."}
            </div>
          )}
        </div>
      ) : (
        /* ──────── Vista PIPELINE (timeline horizontal por PoC) ──────── */
        <div className="space-y-3" data-testid="poc-pipeline">
          <div className="bg-white rounded-xl border border-gray-100 px-5 py-3">
            <p className="text-[11px] text-gray-500 leading-snug flex items-start gap-2">
              <Info size={11} className="text-gray-400 flex-shrink-0 mt-0.5" />
              Línea de tiempo de cada PoC: <strong className="text-gray-700">idea → exploración → piloto → resultado → producción</strong>.
              Los círculos rellenos indican estados ya alcanzados (con la fecha de transición). El círculo verde es el estado actual.
            </p>
          </div>
          {filtered.map(p => <PipelineRow key={p.id} poc={p} onOpen={() => setDetailPoc(p)} onEdit={() => openEdit(p)} onDelete={() => remove(p)} />)}
          {filtered.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-12 bg-white rounded-xl border border-gray-100" data-testid="poc-pipeline-empty">
              <GitBranch size={32} className="mx-auto mb-2 text-gray-300" />
              {pocs.length === 0 ? "Aún no hay PoCs registradas." : "Sin PoCs que coincidan con la búsqueda."}
            </div>
          )}
        </div>
      )}

      {/* ── Modal de DETALLE (timeline + métricas + edición rápida) ── */}
      {detailPoc && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={() => setDetailPoc(null)} data-testid="poc-detail-modal">
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-5 py-3 z-10">
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-1.5 rounded-lg bg-purple-50 text-purple-600 flex-shrink-0"><Beaker size={14} /></div>
                <h3 className="text-sm font-bold truncate" title={detailPoc.title}>{detailPoc.title}</h3>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border flex-shrink-0 ${STATUS_STYLE[detailPoc.status] || STATUS_STYLE.idea}`}>
                  {detailPoc.status}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { openEdit(detailPoc); setDetailPoc(null); }} className="p-1.5 rounded text-gray-500 hover:text-[#007934] hover:bg-green-50" data-testid={`poc-detail-edit-${detailPoc.id}`} title="Editar">
                  <Pencil size={14} />
                </button>
                <button onClick={() => setDetailPoc(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Descripción */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Descripción</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailPoc.desc || <span className="italic text-gray-300">Sin descripción</span>}</p>
              </div>

              {/* Métricas */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-purple-50/50 border border-purple-100 rounded-lg p-3 text-center">
                  <DollarSign size={14} className="mx-auto text-purple-600 mb-1" />
                  <p className="text-sm font-bold text-gray-800">{fmtUSD(detailPoc.cost_estimate_usd)}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Coste Pando</p>
                </div>
                <div className="bg-green-50/50 border border-green-100 rounded-lg p-3 text-center">
                  <TrendingUp size={14} className="mx-auto text-green-600 mb-1" />
                  <p className="text-sm font-bold text-gray-800">{fmtEUR(detailPoc.savings_estimate_eur)}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Ahorro €</p>
                </div>
                <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 text-center">
                  <Hash size={14} className="mx-auto text-blue-600 mb-1" />
                  <p className="text-sm font-bold text-gray-800">{fmtInt(detailPoc.tokens_used)}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tokens</p>
                </div>
              </div>

              {/* Owner & Modelo */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Owner</p>
                  <p className="text-gray-800 font-medium">{detailPoc.owner || "—"}</p>
                </div>
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Modelo Pando</p>
                  <p className="text-gray-800 font-mono text-xs">{detailPoc.model || "—"}</p>
                </div>
              </div>

              {/* Enlaces */}
              {(detailPoc.links || []).length > 0 && (
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-bold">Enlaces</p>
                  <div className="space-y-1">
                    {(detailPoc.links || []).map((l, i) => l.url && (
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline truncate">
                        <ExternalLink size={11} className="flex-shrink-0" />
                        <span className="truncate">{l.label || l.url}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* T17 · Ofertas/proyectos vinculados */}
              {((detailPoc.ofertas_vinculadas || []).length > 0 || (detailPoc.proyectos_vinculados || []).length > 0) && (
                <div className="border border-gray-100 rounded-lg p-3" data-testid="poc-detail-vinculos">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-bold">Enlazado a ofertas/proyectos reales</p>
                  <div className="flex flex-wrap gap-1">
                    {(detailPoc.ofertas_vinculadas || []).map(id => (
                      <span key={id} className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">oferta·{id.slice(0, 8)}</span>
                    ))}
                    {(detailPoc.proyectos_vinculados || []).map(id => (
                      <span key={id} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full">proyecto·{id.slice(0, 8)}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notas */}
              {detailPoc.notes && (
                <div className="border border-gray-100 rounded-lg p-3 bg-amber-50/20">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Notas</p>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{detailPoc.notes}</p>
                </div>
              )}

              {/* Timeline cronológica de transiciones */}
              <div className="border border-gray-100 rounded-lg p-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-3 font-bold flex items-center gap-1.5">
                  <Clock size={11}/> Historial de estados
                </p>
                <PipelineSteps poc={detailPoc} mode="vertical" />
                {(detailPoc.status_history || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic">Sin historial registrado todavía.</p>
                )}
              </div>

              <div className="text-[11px] text-gray-400">
                Creada {detailPoc.created_at?.slice(0,10)} por {detailPoc.created_by_name || detailPoc.created_by || "—"}
                {detailPoc.updated_at && detailPoc.updated_at !== detailPoc.created_at && (
                  <> · Actualizada {detailPoc.updated_at?.slice(0,10)}</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="poc-modal">
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-5 py-3 z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-purple-600" />
                <h3 className="text-sm font-bold">{editing ? "Editar PoC" : "Nueva Prueba de Concepto Pando"}</h3>
              </div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Título *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Ej: Generador automático de memorias técnicas"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                  data-testid="poc-form-title"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Descripción</label>
                <textarea
                  value={form.desc}
                  onChange={e => setForm(f => ({ ...f, desc: e.target.value }))}
                  rows={3}
                  placeholder="Caso de uso, alcance, resultado esperado…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                  data-testid="poc-form-desc"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Estado</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-status"
                  >
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Owner</label>
                  <input
                    type="text"
                    value={form.owner}
                    onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                    placeholder="Nombre Apellido"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-owner"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Modelo Pando</label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                    placeholder="claude-sonnet-4-6"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-model"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Coste estimado Pando (USD)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.cost_estimate_usd}
                    onChange={e => setForm(f => ({ ...f, cost_estimate_usd: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-cost"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Ahorro previsto (€)</label>
                  <input
                    type="number" min="0" step="100"
                    value={form.savings_estimate_eur}
                    onChange={e => setForm(f => ({ ...f, savings_estimate_eur: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-savings"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Tokens consumidos</label>
                  <input
                    type="number" min="0" step="1000"
                    value={form.tokens_used}
                    onChange={e => setForm(f => ({ ...f, tokens_used: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-tokens"
                  />
                </div>
              </div>

              {/* Enlaces */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Enlaces</label>
                  <button onClick={addLink} className="text-[11px] text-[#007934] hover:underline flex items-center gap-1" data-testid="poc-form-add-link">
                    <Plus size={11} /> Añadir enlace
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(form.links || []).map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text" placeholder="Etiqueta"
                        value={l.label || ""}
                        onChange={e => updateLink(i, "label", e.target.value)}
                        className="w-1/3 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#007934]"
                      />
                      <input
                        type="url" placeholder="https://..."
                        value={l.url || ""}
                        onChange={e => updateLink(i, "url", e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#007934]"
                      />
                      <button onClick={() => removeLink(i)} className="text-gray-300 hover:text-red-500 px-2"><X size={14} /></button>
                    </div>
                  ))}
                  {(form.links || []).length === 0 && <p className="text-[11px] text-gray-400 italic">Añade enlaces a notebooks, documentos, demos, etc.</p>}
                </div>
              </div>

              {/* T17 · Aviso de candidatos de título parecido (nunca bloquea, sólo informa) */}
              {!editing && similares.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="poc-similares-warning">
                  <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wide mb-1.5">
                    Ya existen PoCs con título parecido — revisa si esto ya se está explorando
                  </p>
                  <div className="space-y-1">
                    {similares.map(s => (
                      <div key={s.id} className="text-[11px] text-amber-700 flex items-center gap-2" data-testid={`poc-similar-${s.id}`}>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_STYLE[s.status] || STATUS_STYLE.idea}`}>{s.status}</span>
                        <span className="truncate">{s.title}</span>
                        <span className="text-amber-400">({Math.round(s.similitud * 100)}% similar · {s.owner || "sin owner"})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* T17 · Ofertas/proyectos vinculados (IDs reales, validados al guardar) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Ofertas comerciales vinculadas</label>
                  <input
                    type="text" placeholder="Buscar oferta por cliente/objeto…"
                    value={ofertaQuery}
                    onChange={e => setOfertaQuery(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-oferta-search"
                  />
                  {ofertaResults.length > 0 && (
                    <div className="border border-gray-100 rounded-lg mt-1 max-h-32 overflow-y-auto bg-white shadow-sm">
                      {ofertaResults.map(o => (
                        <button key={o.id} type="button" onClick={() => {
                          setForm(f => ({ ...f, ofertas_vinculadas: [...new Set([...(f.ofertas_vinculadas || []), o.id])] }));
                          setOfertaQuery(""); setOfertaResults([]);
                        }} className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-green-50 truncate block" data-testid={`poc-oferta-option-${o.id}`}>
                          {o.cliente} — {(o.objeto_contrato || "").slice(0, 60)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(form.ofertas_vinculadas || []).map(id => (
                      <span key={id} className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid={`poc-oferta-chip-${id}`}>
                        oferta·{id.slice(0, 6)}
                        <button type="button" onClick={() => setForm(f => ({ ...f, ofertas_vinculadas: f.ofertas_vinculadas.filter(x => x !== id) }))}><X size={9}/></button>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Proyectos vinculados</label>
                  <input
                    type="text" placeholder="Buscar proyecto por nombre…"
                    value={proyectoQuery}
                    onChange={e => setProyectoQuery(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#007934]"
                    data-testid="poc-form-proyecto-search"
                  />
                  {proyectoResults.length > 0 && (
                    <div className="border border-gray-100 rounded-lg mt-1 max-h-32 overflow-y-auto bg-white shadow-sm">
                      {proyectoResults.map(p => (
                        <button key={p.id} type="button" onClick={() => {
                          setForm(f => ({ ...f, proyectos_vinculados: [...new Set([...(f.proyectos_vinculados || []), p.id])] }));
                          setProyectoQuery("");
                        }} className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-green-50 truncate block" data-testid={`poc-proyecto-option-${p.id}`}>
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(form.proyectos_vinculados || []).map(id => {
                      const proj = allProjects.find(p => p.id === id);
                      return (
                        <span key={id} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid={`poc-proyecto-chip-${id}`}>
                          {proj?.name || `proyecto·${id.slice(0, 6)}`}
                          <button type="button" onClick={() => setForm(f => ({ ...f, proyectos_vinculados: f.proyectos_vinculados.filter(x => x !== id) }))}><X size={9}/></button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">Notas internas</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Bloqueos, próximos pasos, dependencias…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                  data-testid="poc-form-notes"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setShowForm(false)} className="border border-gray-200 text-gray-600 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={submit}
                disabled={saving || !form.title.trim()}
                className="bg-[#007934] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2"
                data-testid="poc-form-submit"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {editing ? (saving ? "Guardando…" : "Guardar cambios") : (saving ? "Creando…" : "Crear PoC")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-purple-50/40 border border-purple-200 rounded-xl p-4 text-xs text-gray-600">
        <p className="font-semibold text-purple-800 mb-1">¿Cómo funciona?</p>
        <p>
          Las PoCs se persisten en la colección <code className="bg-white border border-purple-200 px-1 rounded">poc_ai_items</code> de MongoDB.
          Estado, owner, métricas (coste Pando en USD, ahorro previsto en €, tokens consumidos) y enlaces se mantienen sincronizados con producción
          al usar el panel de sincronización del Admin.
        </p>
      </div>
    </div>
  );
}

// ── Componentes auxiliares de Pipeline ───────────────────────────────────────

/**
 * Devuelve un mapa { idea: '2026-04-22T...', exploración: '2026-04-23T...', ... }
 * con la primera fecha en que la PoC alcanzó cada estado del pipeline lineal.
 */
function buildReachedMap(poc) {
  const history = poc.status_history || [];
  const reached = {};
  for (const h of history) {
    const st = h.status;
    if (!st) continue;
    if (!reached[st] || (h.at && h.at < reached[st])) {
      reached[st] = h.at || "";
    }
  }
  // Si no hay historial, marcar al menos el estado actual con created_at
  if (!history.length && poc.status) {
    reached[poc.status] = poc.created_at || "";
  }
  return reached;
}

/**
 * Pasos visuales (idea → exploración → piloto → producción) de una PoC.
 * mode: "horizontal" (timeline en pipeline view) o "vertical" (modal detalle)
 */
function PipelineSteps({ poc, mode = "horizontal" }) {
  const reached = buildReachedMap(poc);
  const isDiscarded = poc.status === "descartada";
  const currentIdx = PIPELINE_ORDER.indexOf(poc.status);

  if (mode === "vertical") {
    // Modal: muestra el historial completo cronológico
    const history = (poc.status_history || []).slice().sort((a, b) => (a.at || "").localeCompare(b.at || ""));
    return (
      <div className="space-y-2">
        {history.map((h, i) => (
          <div key={i} className="flex items-start gap-3" data-testid={`history-entry-${i}`}>
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${STATUS_DOT[h.status] || "bg-gray-300"}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[h.status] || STATUS_STYLE.idea}`}>{h.status}</span>
                {h.from_status && <span className="text-[10px] text-gray-400">desde {h.from_status}</span>}
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {h.at?.slice(0,16).replace("T"," ") || "—"}
                {h.by_name && <> · por <strong className="text-gray-700">{h.by_name}</strong></>}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Horizontal: barra de progreso por etapas
  return (
    <div className="flex items-center gap-1 flex-1">
      {PIPELINE_ORDER.map((step, i) => {
        const isReached = !!reached[step];
        const isCurrent = poc.status === step;
        const dotColor = isReached ? STATUS_DOT[step] : "bg-gray-200";
        const ringClass = isCurrent ? "ring-4 ring-green-200" : "";
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center min-w-[70px]" title={isReached ? `Alcanzado ${reached[step]?.slice(0,10)}` : "No alcanzado"}>
              <div className={`w-4 h-4 rounded-full ${dotColor} ${ringClass} transition-all`} />
              <p className={`text-[9px] font-medium mt-1 uppercase tracking-wider ${isReached ? "text-gray-700" : "text-gray-300"}`}>
                {step}
              </p>
              <p className="text-[9px] text-gray-400">
                {isReached && reached[step] ? reached[step].slice(0,10) : "—"}
              </p>
            </div>
            {i < PIPELINE_ORDER.length - 1 && (
              <div className={`flex-1 h-0.5 ${i < currentIdx ? "bg-green-400" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        );
      })}
      {isDiscarded && (
        <div className="ml-3 flex flex-col items-center">
          <div className="w-4 h-4 rounded-full bg-red-500 ring-4 ring-red-200" />
          <p className="text-[9px] font-medium text-red-600 mt-1 uppercase tracking-wider">Descartada</p>
        </div>
      )}
    </div>
  );
}

/**
 * Una fila por PoC en la vista pipeline: cabecera (título + owner + métricas) + timeline horizontal.
 */
function PipelineRow({ poc, onOpen, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all" data-testid={`poc-pipeline-row-${poc.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <button onClick={onOpen} className="text-left flex-1 min-w-0" data-testid={`poc-pipeline-detail-${poc.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[poc.status] || STATUS_STYLE.idea}`}>{poc.status}</span>
            <h4 className="font-semibold text-gray-900 text-sm hover:text-[#007934] transition-colors truncate">{poc.title}</h4>
          </div>
          <p className="text-[11px] text-gray-500 mt-1 truncate flex items-center gap-3">
            <span className="flex items-center gap-1"><Cpu size={10}/> {poc.owner || "—"}</span>
            {poc.cost_estimate_usd > 0  && <span className="text-purple-600">{fmtUSD(poc.cost_estimate_usd)}</span>}
            {poc.savings_estimate_eur > 0 && <span className="text-green-700">{fmtEUR(poc.savings_estimate_eur)}</span>}
            {poc.tokens_used > 0 && <span className="text-blue-600">{fmtInt(poc.tokens_used)} tok</span>}
          </p>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onEdit} className="p-1 rounded text-gray-400 hover:text-[#007934] hover:bg-green-50" title="Editar PoC" data-testid={`poc-pipeline-edit-${poc.id}`}>
            <Pencil size={12} />
          </button>
          <button onClick={onDelete} className="p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50" title="Eliminar PoC" data-testid={`poc-pipeline-delete-${poc.id}`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 px-2">
        <PipelineSteps poc={poc} mode="horizontal" />
      </div>
    </div>
  );
}